import React, { useState } from 'react';
import { Settings, Plus, Trash2, Users, Database, Bell, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function SettingsEditor({ notifications = {}, onChange }) {
  // Безопасная обработка данных
  const safeNotifications = notifications && typeof notifications === 'object' && !Array.isArray(notifications) ? notifications : {};
  
  const managers = Array.isArray(safeNotifications.Managers) ? safeNotifications.Managers : [];
  const allStatuses = safeNotifications.All_Statuses && typeof safeNotifications.All_Statuses === 'object' ? safeNotifications.All_Statuses : {};
  const crmSettings = safeNotifications.Custom_CRM_Settings && typeof safeNotifications.Custom_CRM_Settings === 'object' ? safeNotifications.Custom_CRM_Settings : {};
  const notificationSettings = Array.isArray(safeNotifications.Notification_Settings) ? safeNotifications.Notification_Settings : [];
  
  const statusEntries = Object.entries(allStatuses).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
  
  const [expandedSections, setExpandedSections] = useState({
    statuses: true,
    managers: true,
    crm: true,
    notifications: true
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Обновление всего объекта Notifications
  const updateNotifications = (updates) => {
    onChange({
      ...safeNotifications,
      ...updates
    });
  };
  
  // All_Statuses handlers
  const addStatus = () => {
    const maxKey = statusEntries.length > 0 
      ? Math.max(...statusEntries.map(([k]) => parseInt(k))) 
      : 0;
    const newKey = String(maxKey + 1);
    updateNotifications({
      All_Statuses: {
        ...allStatuses,
        [newKey]: 'New_Status'
      }
    });
  };

  const updateStatus = (key, value) => {
    updateNotifications({
      All_Statuses: {
        ...allStatuses,
        [key]: value
      }
    });
  };

  const deleteStatus = (key) => {
    const newStatuses = { ...allStatuses };
    delete newStatuses[key];
    updateNotifications({ All_Statuses: newStatuses });
  };
  
  // Managers handlers
  const addManager = () => {
    const maxId = managers.length > 0 ? Math.max(...managers.map(m => m.Manager_ID || 0)) : 0;
    updateNotifications({
      Managers: [...managers, {
        Manager_ID: maxId + 1,
        Manager_Name: '',
        Manager_Lang: 'Russian',
        Manager_Contacts_ID: '-',
        Manager_Tags_ID: '',
        Manager_Custom_CRM_ID: '',
        Notification_Channel_Type: 'Telegram',
        Notification_Channel_ID: '-',
        Google_Calendar_ID: '',
        If_Have_Tags: '',
        If_Not_Have_Tags: '',
        Use_Count: ''
      }]
    });
  };
  
  const updateManager = (index, field, value) => {
    const newManagers = [...managers];
    newManagers[index] = { ...newManagers[index], [field]: value };
    updateNotifications({ Managers: newManagers });
  };
  
  const deleteManager = (index) => {
    const newManagers = managers.filter((_, i) => i !== index);
    updateNotifications({ Managers: newManagers });
  };
  
  // CRM Settings handlers
  const updateCRMSetting = (field, value) => {
    updateNotifications({
      Custom_CRM_Settings: {
        ...crmSettings,
        [field]: value
      }
    });
  };
  
  // Notification Settings handlers
  const addNotificationSetting = () => {
    updateNotifications({
      Notification_Settings: [...notificationSettings, {
        Manager_ID: 1,
        Notification_Status: '',
        Stop_Script: 'off',
        Bitrix_Set: null
      }]
    });
  };
  
  const updateNotificationSetting = (index, field, value) => {
    const newSettings = [...notificationSettings];
    newSettings[index] = { ...newSettings[index], [field]: value };
    updateNotifications({ Notification_Settings: newSettings });
  };
  
  const deleteNotificationSetting = (index) => {
    const newSettings = notificationSettings.filter((_, i) => i !== index);
    updateNotifications({ Notification_Settings: newSettings });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Settings className="text-purple-600" size={24} />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-900">Настройки уведомлений</h3>
          <p className="text-sm text-gray-600">Управление статусами, менеджерами и настройками уведомлений</p>
        </div>
      </div>

      {/* All_Statuses Section */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('statuses')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Database className="text-purple-600" size={20} />
            <h4 className="font-semibold text-gray-900">Все статусы (All_Statuses)</h4>
            <span className="text-sm text-gray-500">({statusEntries.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              onClick={(e) => { e.stopPropagation(); addStatus(); }}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1.5 text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus size={14} />
              <span>Добавить</span>
            </motion.button>
            {expandedSections.statuses ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </button>
        
        <AnimatePresence>
          {expandedSections.statuses && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-200"
            >
              <div className="p-6">
                <div className="space-y-3">
                  {statusEntries.map(([key, value]) => (
                    <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-sm font-medium text-gray-600 w-12">{key}:</span>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => updateStatus(key, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Status_Name"
                      />
                      <button
                        onClick={() => deleteStatus(key)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Удалить статус"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {statusEntries.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Нет статусов. Нажмите "Добавить" для создания.
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Managers Section */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('managers')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Users className="text-blue-600" size={20} />
            <h4 className="font-semibold text-gray-900">Менеджеры (Managers)</h4>
            <span className="text-sm text-gray-500">({managers.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              onClick={(e) => { e.stopPropagation(); addManager(); }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus size={14} />
              <span>Добавить</span>
            </motion.button>
            {expandedSections.managers ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </button>
        
        <AnimatePresence>
          {expandedSections.managers && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-200"
            >
              <div className="p-6 space-y-4">
                {managers.map((manager, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-gray-700">Менеджер #{manager.Manager_ID}</span>
                      <button
                        onClick={() => deleteManager(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Удалить менеджера"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Manager_ID</label>
                        <input
                          type="number"
                          value={manager.Manager_ID || ''}
                          onChange={(e) => updateManager(index, 'Manager_ID', parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Manager_Name</label>
                        <input
                          type="text"
                          value={manager.Manager_Name || ''}
                          onChange={(e) => updateManager(index, 'Manager_Name', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Manager_Lang</label>
                        <select
                          value={manager.Manager_Lang || 'Russian'}
                          onChange={(e) => updateManager(index, 'Manager_Lang', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Russian">Russian</option>
                          <option value="English">English</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notification_Channel_Type</label>
                        <select
                          value={manager.Notification_Channel_Type || 'Telegram'}
                          onChange={(e) => updateManager(index, 'Notification_Channel_Type', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Telegram">Telegram</option>
                          <option value="Email">Email</option>
                          <option value="SMS">SMS</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notification_Channel_ID</label>
                        <input
                          type="text"
                          value={manager.Notification_Channel_ID || ''}
                          onChange={(e) => updateManager(index, 'Notification_Channel_ID', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Manager_Contacts_ID</label>
                        <input
                          type="text"
                          value={manager.Manager_Contacts_ID || ''}
                          onChange={(e) => updateManager(index, 'Manager_Contacts_ID', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {managers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Нет менеджеров. Нажмите "Добавить" для создания.
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Custom CRM Settings Section */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('crm')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Database className="text-green-600" size={20} />
            <h4 className="font-semibold text-gray-900">Настройки CRM (Custom_CRM_Settings)</h4>
          </div>
          {expandedSections.crm ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        
        <AnimatePresence>
          {expandedSections.crm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-200"
            >
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CRM</label>
                    <select
                      value={crmSettings.CRM || 'Bitrix'}
                      onChange={(e) => updateCRMSetting('CRM', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    >
                      <option value="Bitrix">Bitrix</option>
                      <option value="AmoCRM">AmoCRM</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Domane_Name</label>
                    <input
                      type="text"
                      value={crmSettings.Domane_Name || ''}
                      onChange={(e) => updateCRMSetting('Domane_Name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      placeholder="domain.bitrix24.ru"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">API_Token</label>
                    <input
                      type="text"
                      value={crmSettings.API_Token || ''}
                      onChange={(e) => updateCRMSetting('API_Token', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      placeholder="API Token"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bitrix_ID</label>
                    <input
                      type="text"
                      value={crmSettings.Bitrix_ID || ''}
                      onChange={(e) => updateCRMSetting('Bitrix_ID', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Notification Settings Section */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('notifications')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Bell className="text-orange-600" size={20} />
            <h4 className="font-semibold text-gray-900">Настройки уведомлений (Notification_Settings)</h4>
            <span className="text-sm text-gray-500">({notificationSettings.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              onClick={(e) => { e.stopPropagation(); addNotificationSetting(); }}
              className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1.5 text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus size={14} />
              <span>Добавить</span>
            </motion.button>
            {expandedSections.notifications ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </button>
        
        <AnimatePresence>
          {expandedSections.notifications && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-200"
            >
              <div className="p-6 space-y-3">
                {notificationSettings.map((setting, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-gray-700">Настройка #{index + 1}</span>
                      <button
                        onClick={() => deleteNotificationSetting(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Удалить настройку"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Manager_ID</label>
                        <input
                          type="number"
                          value={setting.Manager_ID || 1}
                          onChange={(e) => updateNotificationSetting(index, 'Manager_ID', parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notification_Status</label>
                        <input
                          type="text"
                          value={setting.Notification_Status || ''}
                          onChange={(e) => updateNotificationSetting(index, 'Notification_Status', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Stop_Script</label>
                        <select
                          value={setting.Stop_Script || 'off'}
                          onChange={(e) => updateNotificationSetting(index, 'Stop_Script', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="on">On</option>
                          <option value="off">Off</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                {notificationSettings.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Нет настроек. Нажмите "Добавить" для создания.
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
